/**
 * Review deep-link query grammar. No `window` access — SSR reads it for the robots tag, the
 * reviews section reads it in the browser to open the drawer / gallery.
 *
 *   ?reviews=all                     → reviews view open
 *   ?review_id=<id>&review_media=<n> → that review's nth media item, 1-based
 *
 * `n` counts media, not photos: the gallery indexes images and videos as one list.
 */

/** Canonical names — the cross-repo contract (web, iOS, Android). */
export const REVIEW_PARAMS = {
  ALL: "reviews",
  REVIEW_ID: "review_id",
  MEDIA: "review_media",
} as const;

export const REVIEW_ALL_VALUE = "all";

/** Widest scope first. Cosmetic to the parser, but these links get copied out of the address bar. */
const REVIEW_PARAM_ORDER: string[] = Object.values(REVIEW_PARAMS);

/** Read, never written, so already-shared links keep working and get upgraded in place. */
export const LEGACY_REVIEW_PARAMS = {
  OPEN: "open-reviews",
  ALL: "review-all",
} as const;

/** Tried after the canonical name. `img` is what requirement.md §4 still specifies. */
export const LEGACY_MEDIA_PARAMS = ["review_image", "img"] as const;

/** Every param this feature reads — used to key the SSR HTML cache. */
export const REVIEW_DEEP_LINK_QUERY_PARAMS: string[] = [
  ...REVIEW_PARAM_ORDER,
  ...Object.values(LEGACY_REVIEW_PARAMS),
  ...LEGACY_MEDIA_PARAMS,
];

export interface ParsedReviewParams {
  /** Any review param present. A bare `review_id` counts — FR-13/14 fall back to the drawer. */
  openReviews: boolean;
  reviewId: string | null;
  /** Raw value; the gallery validates it against the review's media count. */
  mediaNumber: string | null;
}

export function parseReviewParams(search: string): ParsedReviewParams {
  // Accepts a bare query string, "?..." or a full/relative URL.
  const queryStart = (search || "").indexOf("?");
  const params = new URLSearchParams(
    queryStart === -1 ? search || "" : search.slice(queryStart + 1),
  );
  const reviewId = params.get(REVIEW_PARAMS.REVIEW_ID);
  const mediaNumber = LEGACY_MEDIA_PARAMS.reduce<string | null>(
    (found, key) => found ?? params.get(key),
    params.get(REVIEW_PARAMS.MEDIA),
  );
  const allFlag =
    params.get(REVIEW_PARAMS.ALL) === REVIEW_ALL_VALUE ||
    params.get(LEGACY_REVIEW_PARAMS.ALL) === "true" ||
    params.get(LEGACY_REVIEW_PARAMS.OPEN) === "true";

  return {
    openReviews: allFlag || !!reviewId,
    reviewId: reviewId || null,
    mediaNumber: mediaNumber || null,
  };
}

/**
 * Sort the review params into REVIEW_PARAM_ORDER, in place.
 *
 * `URLSearchParams.set` appends a key that is not already present, so without this the order
 * depends on which writer ran first. Only review params move; everything else keeps its place.
 */
export function applyReviewParamOrder(params: URLSearchParams): void {
  const present = REVIEW_PARAM_ORDER.map((key) => [key, params.get(key)] as const).filter(
    ([, value]) => value !== null,
  );
  if (!present.length) return;
  REVIEW_PARAM_ORDER.forEach((key) => params.delete(key));
  present.forEach(([key, value]) => params.append(key, value as string));
}
